import { Api } from '@monorepo/shared/apiClient';
import {
  invalidateLogEntryCache,
  LogEntryCacheEvents,
} from '@monorepo/shared/cacheRegisters/data2/logEntryCacheRegister';
import { Loading } from '@monorepo/shared/componentsV2/Loading';
import { LogEntry } from '@monorepo/shared/componentsV2/logs/types';
import { useToast } from '@monorepo/shared/contexts';
import { useConfirmIncompleteLogEntries } from '@monorepo/shared/hooks/logs/useConfirmIncompleteLogEntries';
import { useEdpAiSubmission } from '@monorepo/shared/hooks/logs/useEdpAiSubmission';
import { useLogEntryFields } from '@monorepo/shared/hooks/logs/useLogFields';
import { useHasProjectUpdatePermissions } from '@monorepo/shared/hooks/permissions/useHasPermissions';
import { ValidationErrors } from 'final-form';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  convertLogEntriesFormFieldValuesToSaveLogEntryRequests,
  LogEntriesFormValues,
} from './conversionHelpers';
import { useCloseWithConfirmation } from './hooks/useCloseWithConfirmation';
import { useModalActionLeftButtons } from './hooks/useModalActionLeftButtons';
import { type ExistingFile } from './hooks/useUploadedFiles';
import { LogEntriesEditManyModalPage } from './pages/LogEntriesEditManyModalPage';
import { LogEntriesTableReviewModalPage } from './pages/LogEntriesTableReviewModalPage';
import { AiSubmissionPages } from './shared';
import { StyledModalWizard } from './styled';
import { validateLogEntriesFormValues } from './utils/validateLogEntriesFormValues';

type LogEntriesExistingAiSubmissionsModalProps = {
  edpAiSubmissionId: string;
  logEntryId?: string;
  logId: string;
  onClose: () => void;
  organizationId: string;
  projectId: string;
  isReadOnly?: boolean;
};

export function LogEntriesExistingAiSubmissionsModal({
  edpAiSubmissionId,
  logEntryId,
  logId,
  onClose,
  organizationId,
  projectId,
  isReadOnly: isReadOnlyProp = false,
}: LogEntriesExistingAiSubmissionsModalProps) {
  const { edpAiSubmission, isLoading, error } = useEdpAiSubmission({
    organizationId,
    logId,
    projectId,
    edpAiSubmissionId,
    config: { useErrorBoundary: false },
  });

  const { showUserFriendlyErrorToast, success: showSuccessToast } = useToast();
  useEffect(() => {
    if (!error || edpAiSubmission) return;
    showUserFriendlyErrorToast(
      error,
      "Couldn't open this AI submission. It may have been deleted.",
    );
    onClose();
  }, [edpAiSubmission, error, onClose, showUserFriendlyErrorToast]);

  const { logEntries: serverLogEntries, files } = edpAiSubmission ?? {};
  const [localLogEntries, setLocalLogEntries] = useState<LogEntry[] | null>(
    null,
  );
  const logEntries = localLogEntries ?? serverLogEntries;
  const [logEntryIDsToDelete, setLogEntryIDsToDelete] = useState<string[]>([]);

  const persistedLogEntryIds = useMemo(
    () => new Set((serverLogEntries ?? []).map((entry) => entry.id)),
    [serverLogEntries],
  );

  // when a user deletes an entry manually from the modal
  const locallyDeletedPersistedLogEntryIds = useMemo(() => {
    if (!localLogEntries) return [];
    const localIds = new Set(localLogEntries.map((entry) => entry.id));
    return [...persistedLogEntryIds].filter((id) => !localIds.has(id));
  }, [localLogEntries, persistedLogEntryIds]);

  const canSubmitDataForProject = useHasProjectUpdatePermissions(projectId);
  const isReadOnly = isReadOnlyProp || !canSubmitDataForProject;
  const { logFields } = useLogEntryFields({
    organizationId,
    logId,
  });
  const validate = useCallback(
    (values: LogEntriesFormValues) =>
      validateLogEntriesFormValues(values, logFields),
    [logFields],
  );
  const { submitWithIncompleteChecks } = useConfirmIncompleteLogEntries();

  const selectedFile = useMemo(
    (): ExistingFile => ({
      type: 'ExistingFile',
      logEntries: logEntries ?? [],
      fileBlobUrl: files?.[0]?.signedUrl,
      mimeType: files?.[0]?.mimeType,
    }),
    [files, logEntries],
  );

  const { renderModalActionLeftButtons, logEntryIndex, setLogEntryIndex } =
    useModalActionLeftButtons({
      logEntries,
      setLogEntries: setLocalLogEntries,
      logEntryIDsToDelete,
      setLogEntryIDsToDelete,
      isReadOnly,
      deleteConfirmationDescription:
        'This entry will be permanently removed from the log once you save your changes.',
      entryFormPageNumber: AiSubmissionPages.EDIT_ENTRIES_PAGE,
      reviewPageNumber: AiSubmissionPages.REVIEW_PAGE,
      initialLogEntryId: logEntryId,
      edpAiSubmissionId,
    });

  const [editedLogEntryIds, setEditedLogEntryIds] = useState(
    () => new Set<string>(),
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Reviewing an entry is not a form edit, so it can't be tracked through RFF's
  // dirty fields; compare the local reviewer against what the server has.
  const newlyReviewedLogEntryIds = useMemo(() => {
    const unreviewedServerIds = new Set(
      (serverLogEntries ?? [])
        .filter((entry) => !entry.reviewedBy)
        .map((entry) => entry.id),
    );
    return new Set(
      (localLogEntries ?? [])
        .filter(
          (entry) => entry.reviewedBy && unreviewedServerIds.has(entry.id),
        )
        .map((entry) => entry.id),
    );
  }, [localLogEntries, serverLogEntries]);

  const hasAddedLogEntries = (localLogEntries ?? []).some(
    (entry) => !persistedLogEntryIds.has(entry.id),
  );

  const hasUnsavedChanges =
    editedLogEntryIds.size > 0 ||
    newlyReviewedLogEntryIds.size > 0 ||
    locallyDeletedPersistedLogEntryIds.length > 0 ||
    hasAddedLogEntries;

  const { onCloseWithConfirmation, closeWithoutConfirmation } =
    useCloseWithConfirmation({
      requiresConfirmation: canSubmitDataForProject && hasUnsavedChanges,
      unsavedWorkMessage: 'Your changes have not been saved.',
      onClose,
    });

  const saveButtonText = useMemo(() => {
    const hasConfirmations = newlyReviewedLogEntryIds.size > 0;
    const hasFieldEdits = editedLogEntryIds.size > 0;

    if (hasConfirmations && hasFieldEdits) return 'Save and confirm entries';
    if (hasConfirmations) return 'Confirm entries';
    return 'Save';
  }, [editedLogEntryIds, newlyReviewedLogEntryIds]);

  // Persist edited/reviewed existing rows and every new row, even empty ones, so
  // they go through the incomplete-entry confirm.
  const getFormValuesToSave = useCallback(
    (values: Partial<LogEntriesFormValues>): Partial<LogEntriesFormValues> =>
      Object.fromEntries(
        Object.entries(values).filter(([id, entryValues]) => {
          // Deleted rows leave values behind in form state
          if (!logEntries?.some((entry) => entry.id === id)) return false;

          if (persistedLogEntryIds.has(id)) {
            return (
              editedLogEntryIds.has(id) || newlyReviewedLogEntryIds.has(id)
            );
          }
          return !!entryValues;
        }),
      ),
    [
      editedLogEntryIds,
      logEntries,
      newlyReviewedLogEntryIds,
      persistedLogEntryIds,
    ],
  );

  const handleSaveSuccess = useCallback(
    async ({
      createdCount,
      updatedCount,
      deletedCount,
    }: {
      createdCount: number;
      updatedCount: number;
      deletedCount: number;
    }) => {
      if (createdCount > 0) {
        invalidateLogEntryCache(LogEntryCacheEvents.CREATE, {
          organizationId,
          logId,
          projectId,
        });
      }
      if (updatedCount > 0) {
        invalidateLogEntryCache(LogEntryCacheEvents.UPDATE, {
          organizationId,
          logId,
          projectId,
        });
      }
      if (deletedCount > 0) {
        invalidateLogEntryCache(LogEntryCacheEvents.DELETE, {
          organizationId,
          logId,
          projectId,
        });
      }

      const successCount = createdCount + updatedCount + deletedCount;
      const logEntryString = successCount === 1 ? 'Log Entry' : 'Log Entries';

      let actionVerb = 'modified';
      if (createdCount > 0 && updatedCount === 0 && deletedCount === 0) {
        actionVerb = 'saved';
      } else if (updatedCount > 0 && createdCount === 0 && deletedCount === 0) {
        actionVerb = 'updated';
      } else if (deletedCount > 0 && createdCount === 0 && updatedCount === 0) {
        actionVerb = 'deleted';
      }

      showSuccessToast(
        `Successfully ${actionVerb} ${successCount} ${logEntryString}!`,
      );
      closeWithoutConfirmation();
    },
    [
      closeWithoutConfirmation,
      logId,
      organizationId,
      projectId,
      showSuccessToast,
    ],
  );

  const saveEditedLogEntries = useCallback(
    async (values: LogEntriesFormValues) => {
      if (!logFields) {
        showUserFriendlyErrorToast(
          new Error('Failed to load log field options.'),
          'Unable to save log entries.',
          { dontAutoHide: true },
        );
        return;
      }

      const requestsWithClientIds = Object.values(
        convertLogEntriesFormFieldValuesToSaveLogEntryRequests({
          logEntries: logEntries ?? [],
          formValues: getFormValuesToSave(values),
          logFields,
        }),
      );

      if (
        requestsWithClientIds.length === 0 &&
        locallyDeletedPersistedLogEntryIds.length === 0
      ) {
        return;
      }

      const createdCount = requestsWithClientIds.filter(
        (request) => !request.id || !persistedLogEntryIds.has(request.id),
      ).length;
      const updatedCount = requestsWithClientIds.length - createdCount;
      const deletedCount = locallyDeletedPersistedLogEntryIds.length;

      const saveLogEntryRequests = requestsWithClientIds.map((request) =>
        request.id && persistedLogEntryIds.has(request.id)
          ? request
          : { ...request, id: undefined },
      );

      const successCount = createdCount + updatedCount + deletedCount;
      const logEntryString = successCount === 1 ? 'Log Entry' : 'Log Entries';
      let actionVerb = 'delete';
      if (createdCount > 0) {
        actionVerb = 'save';
      } else if (updatedCount > 0) {
        actionVerb = 'update';
      }

      setIsSubmitting(true);
      try {
        await Api.saveAndDeleteManyLogEntries({
          edpAiSubmissionId,
          saveLogEntryRequests,
          logEntryIdsToDelete: locallyDeletedPersistedLogEntryIds,
          logId,
          organizationId,
          projectId,
        });
        await handleSaveSuccess({ createdCount, updatedCount, deletedCount });
      } catch (submitError) {
        showUserFriendlyErrorToast(
          submitError,
          `Unable to ${actionVerb} ${logEntryString}.`,
          {
            dontAutoHide: true,
          },
        );
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      locallyDeletedPersistedLogEntryIds,
      edpAiSubmissionId,
      getFormValuesToSave,
      handleSaveSuccess,
      logEntries,
      logFields,
      logId,
      organizationId,
      persistedLogEntryIds,
      projectId,
      showUserFriendlyErrorToast,
    ],
  );

  const handleSave = useCallback(
    (values: LogEntriesFormValues, errors: ValidationErrors) => {
      const entriesErrors = Object.keys(getFormValuesToSave(values)).map(
        (entryId) => errors?.[entryId] ?? {},
      );
      return submitWithIncompleteChecks({
        entriesErrors,
        submit: () => saveEditedLogEntries(values),
      });
    },
    [getFormValuesToSave, saveEditedLogEntries, submitWithIncompleteChecks],
  );

  const wizardPages = useMemo(
    () =>
      isLoading
        ? [<Loading />]
        : [
            <LogEntriesEditManyModalPage
              selectedFile={selectedFile}
              logEntryIndex={logEntryIndex}
              setLogEntryIndex={setLogEntryIndex}
              logId={logId}
              organizationId={organizationId}
              projectId={projectId}
              isReadOnly={isReadOnly}
              isSubmitting={isSubmitting}
              setEditedLogEntryIds={setEditedLogEntryIds}
              setLogEntries={setLocalLogEntries}
            />,
            <LogEntriesTableReviewModalPage
              selectedFile={selectedFile}
              logId={logId}
              organizationId={organizationId}
              projectId={projectId}
              isReadOnly={isReadOnly}
              isSubmitting={isSubmitting}
              setEditedLogEntryIds={setEditedLogEntryIds}
              setLogEntries={setLocalLogEntries}
              logEntryIDsToDelete={logEntryIDsToDelete}
              setLogEntryIDsToDelete={setLogEntryIDsToDelete}
            />,
          ],
    [
      isLoading,
      selectedFile,
      logEntryIndex,
      setLogEntryIndex,
      logId,
      organizationId,
      projectId,
      isReadOnly,
      isSubmitting,
      logEntryIDsToDelete,
    ],
  );

  if (error && !edpAiSubmission) return null;

  return (
    <StyledModalWizard
      addArrayMutators
      isEditing={false}
      renderActionsLeft={renderModalActionLeftButtons}
      itemLabel="Log Entries"
      onClose={onCloseWithConfirmation}
      onPageSubmit={handleSave}
      showSaveOnIntermediatePages
      validateForm={validate}
      shouldBypassValidationOnNextPage
      // Confirming an entry doesn't touch the form, so a review-only save is pristine.
      allowPristineSubmit
      isSaveButtonDisabled={!logFields || !hasUnsavedChanges}
      isSubmitting={isSubmitting}
      hideSaveButton={isReadOnly}
      shouldSaveButtonAppearAfterNext
      nextButtonVariant="outlined"
      nextButtonText="Review summary"
      saveButtonText={saveButtonText}
      completionButtonText={saveButtonText}
      parentHandlesSuccessAndError
      showBackButton
      title="Log Entries"
      wizardPages={wizardPages}
      maxWidth="xl"
      fullHeight
    />
  );
}
